import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Lock, Edit, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * A masked sensitive field that requires the owner to enter the last 6
 * characters of the current value before editing is permitted.
 */
const SensitiveField = ({ label, maskedValue, fieldKey, onVerified, icon: Icon }) => {
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [suffix, setSuffix] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (!suffix || suffix.length < 4) {
      toast.error('Please enter at least 4 characters');
      return;
    }
    setVerifying(true);
    try {
      const response = await axios.post(`${API}/users/me/verify-field`, {
        field: fieldKey,
        suffix
      });
      if (response.data.verified) {
        setShowVerifyDialog(false);
        setSuffix('');
        onVerified(response.data.current_value);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setSuffix('');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div className="space-y-1">
        <Label className="flex items-center gap-1">
          {label}
          <Lock className="w-3 h-3 text-amber-500" />
        </Label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              value={maskedValue || ''}
              disabled
              className="bg-amber-50 border-amber-200 text-gray-600 pr-10"
            />
            {Icon && <Icon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowVerifyDialog(true)}
            className="flex-shrink-0 border-[#194a50]/30 text-[#194a50] hover:bg-[#194a50]/5"
          >
            <Edit className="w-3.5 h-3.5 mr-1" />
            Edit
          </Button>
        </div>
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" />
          Verify ownership to edit this field
        </p>
      </div>

      <Dialog
        open={showVerifyDialog}
        onOpenChange={(open) => { setShowVerifyDialog(open); if (!open) setSuffix(''); }}
      >
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#194a50]" />
              Verify {label}
            </DialogTitle>
            <DialogDescription>
              Enter the <strong>last 6 characters</strong> of your current{' '}
              {label.toLowerCase()} to confirm you know it.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              Current value: <span className="font-mono font-medium">{maskedValue}</span>
            </div>
            <div className="space-y-2">
              <Label>Enter the last 6 characters / digits</Label>
              <Input
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="e.g. 123456"
                maxLength={10}
                onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVerifyDialog(false); setSuffix(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleVerify}
              disabled={verifying || !suffix}
              className="bg-[#194a50] hover:bg-[#10555e]"
            >
              {verifying
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</>
                : 'Verify & Edit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SensitiveField;
